import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  generatePath,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useParams,
} from 'react-router';

import { AppShell, MemberAreaLayout, PublicShell, SplashScreen } from './components/AppShell';
import { ActiveTreeProvider } from './lib/active-tree';
import { api, ApiError, refreshSession, session } from './lib/api';
import { FORGOT_PASSWORD_ENABLED, WITHDRAW_ENABLED } from './lib/flags';
import { useSessionStatus } from './lib/session';
import { HomePage } from './pages/Home';
import { LoginPage } from './pages/Login';

// Beranda (/home) dan login dimuat langsung (layar pertama); layar lain dipecah per chunk.
const AcceptTermsPage = lazy(() =>
  import('./pages/AcceptTerms').then((m) => ({ default: m.AcceptTermsPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })),
);
const NetworkPage = lazy(() => import('./pages/Network').then((m) => ({ default: m.NetworkPage })));
const NotificationsPage = lazy(() =>
  import('./pages/Notifications').then((m) => ({ default: m.NotificationsPage })),
);
const ProfilePage = lazy(() => import('./pages/Profile').then((m) => ({ default: m.ProfilePage })));
const RegisterPage = lazy(() => import('./pages/Register').then((m) => ({ default: m.RegisterPage })));
const ShopPage = lazy(() => import('./pages/Shop').then((m) => ({ default: m.ShopPage })));
const TransactionDetailPage = lazy(() =>
  import('./pages/TransactionDetail').then((m) => ({ default: m.TransactionDetailPage })),
);
const TransactionsPage = lazy(() =>
  import('./pages/Transactions').then((m) => ({ default: m.TransactionsPage })),
);
const TreesPage = lazy(() => import('./pages/Trees').then((m) => ({ default: m.TreesPage })));
const PayTreePage = lazy(() => import('./pages/Trees').then((m) => ({ default: m.PayTreePage })));
const WalletPage = lazy(() => import('./pages/Wallet').then((m) => ({ default: m.WalletPage })));
const WithdrawPage = lazy(() => import('./pages/Withdraw').then((m) => ({ default: m.WithdrawPage })));
const AccessPage = lazy(() => import('./pages/Access').then((m) => ({ default: m.AccessPage })));
const PoinPage = lazy(() => import('./pages/Poin').then((m) => ({ default: m.PoinPage })));
const PerformancePage = lazy(() =>
  import('./pages/Performance').then((m) => ({ default: m.PerformancePage })),
);
const ComplaintPage = lazy(() => import('./pages/Complaint').then((m) => ({ default: m.ComplaintPage })));
const FunPage = lazy(() => import('./pages/Fun').then((m) => ({ default: m.FunPage })));
const LifestylePage = lazy(() => import('./pages/Lifestyle').then((m) => ({ default: m.LifestylePage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});

// Keluar: buang semua data milik sesi sebelumnya.
session.subscribe(() => {
  if (session.status() === 'anonymous') queryClient.clear();
});

function RequireAuth() {
  const status = useSessionStatus();
  const location = useLocation();
  // Akun impor praregis yang belum menyetujui S&K: area member diganti layar persetujuan. Layar lain tidak
  // menunggu /me (data yang sama dipakai AppShell dan beranda), jadi member biasa tidak tertunda.
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('me'), enabled: status === 'authenticated' });
  if (status === 'unknown') return <SplashScreen />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.data?.terms.accepted === false) {
    return (
      <Suspense fallback={<SplashScreen />}>
        <AcceptTermsPage />
      </Suspense>
    );
  }
  return (
    <ActiveTreeProvider>
      <Outlet />
    </ActiveTreeProvider>
  );
}

/** Halaman yang boleh dibuka dengan atau tanpa login (mis. pengaduan): kerangka mengikuti status sesi. */
function AnyAuth() {
  const status = useSessionStatus();
  if (status === 'unknown') return <SplashScreen />;
  return status === 'authenticated' ? (
    <ActiveTreeProvider>
      <AppShell />
    </ActiveTreeProvider>
  ) : (
    <PublicShell />
  );
}

function PublicOnly() {
  const status = useSessionStatus();
  const location = useLocation();
  if (status === 'unknown') return <SplashScreen />;
  return status === 'authenticated' && !location.pathname.startsWith('/register') ? (
    <Navigate to="/home" replace />
  ) : (
    <Outlet />
  );
}

// Rute lama berbahasa Indonesia tetap dialihkan: link referral (/daftar?ref=…) dan bookmark sudah tersebar.
const LEGACY_ROUTES: [from: string, to: string][] = [
  ['/masuk', '/login'],
  ['/daftar', '/register'],
  ['/lupa-password', '/forgot-password'],
  ['/beranda', '/home'],
  ['/belanja', '/shop'],
  ['/poin', '/points'],
  ['/dompet', '/wallet'],
  ['/dompet/cairkan', '/wallet/withdraw'],
  ['/jaringan', '/network'],
  ['/performa', '/performance'],
  ['/transaksi', '/transactions'],
  ['/transaksi/:id', '/transactions/:id'],
  ['/transaksi/item/:itemId', '/transactions/item/:itemId'],
  ['/profil', '/profile'],
  ['/pohon', '/my-trees'],
  ['/pohon/:id/bayar', '/my-trees/:id/pay'],
  ['/pohon/:id/upgrade', '/my-trees/:id/upgrade'],
  ['/notifikasi', '/inbox'],
];

function LegacyRedirect({ to }: { to: string }) {
  const params = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: generatePath(to, params), search, hash }} replace />;
}

// /my-trees dan /inbox (bukan /trees, /notifications): prefix itu milik member-api (vite.config.ts, nginx.conf).
const router = createBrowserRouter([
  ...LEGACY_ROUTES.map(([path, to]) => ({ path, element: <LegacyRedirect to={to} /> })),
  {
    element: <PublicOnly />,
    children: [
      {
        element: <PublicShell />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          {
            path: '/forgot-password',
            element: FORGOT_PASSWORD_ENABLED ? <ForgotPasswordPage /> : <Navigate to="/login" replace />,
          },
        ],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/home', element: <HomePage /> },
          { path: '/fun', element: <FunPage /> },
          { path: '/shop', element: <ShopPage /> },
          { path: '/lifestyle', element: <LifestylePage /> },
          {
            element: <MemberAreaLayout />,
            children: [
              { path: '/access', element: <AccessPage /> },
              { path: '/points', element: <PoinPage /> },
              { path: '/wallet', element: <WalletPage /> },
              { path: '/network', element: <NetworkPage /> },
              { path: '/performance', element: <PerformancePage /> },
            ],
          },
          { path: '/transactions', element: <TransactionsPage /> },
          { path: '/transactions/:id', element: <TransactionDetailPage /> },
          { path: '/transactions/item/:itemId', element: <TransactionDetailPage /> },
          {
            path: '/wallet/withdraw',
            element: WITHDRAW_ENABLED ? <WithdrawPage /> : <Navigate to="/wallet" replace />,
          },
          { path: '/profile', element: <ProfilePage /> },
          { path: '/my-trees', element: <TreesPage /> },
          { path: '/my-trees/:id/pay', element: <PayTreePage /> },
          { path: '/my-trees/:id/upgrade', element: <PayTreePage /> },
          { path: '/inbox', element: <NotificationsPage /> },
        ],
      },
    ],
  },
  { element: <AnyAuth />, children: [{ path: '/pengaduan', element: <ComplaintPage /> }] },
  { path: '*', element: <Navigate to="/home" replace /> },
]);

export function App() {
  useEffect(() => {
    void refreshSession();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
